import { useState } from "react";
import { Sidebar } from "@/components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Database, Cloud, Link2, CheckCircle, Clock, AlertCircle, RefreshCw,
  Car, Users, DollarSign, Wrench, Megaphone, Box, BarChart3, MessageSquare,
  FileText, CreditCard, Globe, Phone, Mail, Shield
} from "lucide-react";

// Integration definitions matching the NADA document
const INTEGRATIONS = {
  dms: {
    title: "DMS Systems",
    description: "Core Dealer Management Systems",
    icon: Database,
    color: "text-blue-500",
    integrations: [
      { name: "CDK Global", status: "connected", lastSync: "2 min ago", records: "12,450" },
      { name: "Reynolds & Reynolds", status: "available", lastSync: null, records: null },
      { name: "Dealertrack DMS", status: "available", lastSync: null, records: null },
      { name: "Tekion", status: "available", lastSync: null, records: null },
    ]
  },
  crm: {
    title: "CRM Systems",
    description: "Customer Relationship Management",
    icon: Users,
    color: "text-emerald-500",
    integrations: [
      { name: "DealerSocket", status: "connected", lastSync: "5 min ago", records: "8,234" },
      { name: "VinSolutions", status: "available", lastSync: null, records: null },
      { name: "eLead", status: "available", lastSync: null, records: null },
    ]
  },
  fi: {
    title: "F&I & Lender",
    description: "Finance, Insurance & Lender Networks",
    icon: DollarSign,
    color: "text-amber-500",
    integrations: [
      { name: "RouteOne", status: "connected", lastSync: "1 min ago", records: "456" },
      { name: "Dealertrack F&I", status: "connected", lastSync: "3 min ago", records: "389" },
    ]
  },
  inventory: {
    title: "Inventory & Pricing",
    description: "Inventory Management & Wholesale",
    icon: Car,
    color: "text-purple-500",
    integrations: [
      { name: "vAuto", status: "connected", lastSync: "Real-time", records: "287" },
      { name: "Manheim", status: "connected", lastSync: "15 min ago", records: "1,250" },
      { name: "ACV Auctions", status: "available", lastSync: null, records: null },
    ]
  },
  service: {
    title: "Fixed Ops / Service",
    description: "Service Scheduling & Lane Technology",
    icon: Wrench,
    color: "text-cyan-500",
    integrations: [
      { name: "Xtime", status: "connected", lastSync: "Real-time", records: "892" },
      { name: "DealerFX", status: "available", lastSync: null, records: null },
    ]
  },
  marketing: {
    title: "Marketing & Marketplaces",
    description: "Digital Marketing & Vehicle Marketplaces",
    icon: Globe,
    color: "text-pink-500",
    integrations: [
      { name: "Dealer.com", status: "connected", lastSync: "10 min ago", records: "45K visits" },
      { name: "Cars.com", status: "connected", lastSync: "30 min ago", records: "156 leads" },
      { name: "AutoTrader", status: "connected", lastSync: "30 min ago", records: "189 leads" },
      { name: "CarGurus", status: "connected", lastSync: "30 min ago", records: "134 leads" },
      { name: "TrueCar", status: "available", lastSync: null, records: null },
    ]
  },
  communications: {
    title: "Communications",
    description: "Voice, SMS & Messaging Platforms",
    icon: Phone,
    color: "text-green-500",
    integrations: [
      { name: "Twilio", status: "connected", lastSync: "Real-time", records: "2,340 msgs" },
      { name: "RingCentral", status: "available", lastSync: null, records: null },
      { name: "Podium", status: "connected", lastSync: "5 min ago", records: "156 reviews" },
    ]
  },
  documents: {
    title: "Documents & Payments",
    description: "eSignatures, Payments & Compliance",
    icon: FileText,
    color: "text-orange-500",
    integrations: [
      { name: "DocuSign", status: "connected", lastSync: "Real-time", records: "234 docs" },
      { name: "OneSpan", status: "available", lastSync: null, records: null },
      { name: "Worldpay", status: "connected", lastSync: "Real-time", records: "$1.2M" },
    ]
  },
  analytics: {
    title: "Data & Analytics",
    description: "Business Intelligence & Data Platforms",
    icon: BarChart3,
    color: "text-indigo-500",
    integrations: [
      { name: "Snowflake", status: "connected", lastSync: "Hourly", records: "2.5M rows" },
      { name: "Databricks", status: "available", lastSync: null, records: null },
      { name: "Power BI", status: "connected", lastSync: "Daily", records: "12 reports" },
      { name: "Tableau", status: "available", lastSync: null, records: null },
    ]
  },
};

const getStatusBadge = (status) => {
  if (status === "connected") {
    return <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20"><CheckCircle className="w-3 h-3 mr-1" /> Connected</Badge>;
  }
  if (status === "syncing") {
    return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20"><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Syncing</Badge>;
  }
  return <Badge className="bg-slate-500/10 text-slate-400 border-slate-500/20"><Clock className="w-3 h-3 mr-1" /> Available</Badge>;
};

export default function IntegrationsPage() {
  const [activeCategory, setActiveCategory] = useState("all");

  const connectedCount = Object.values(INTEGRATIONS).reduce((acc, cat) => 
    acc + cat.integrations.filter(i => i.status === "connected").length, 0
  );
  const totalCount = Object.values(INTEGRATIONS).reduce((acc, cat) => 
    acc + cat.integrations.length, 0
  );

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3" data-testid="integrations-title">
              <Link2 className="w-8 h-8 text-blue-500" />
              Integrations
            </h1>
            <p className="text-muted-foreground">Connect your dealership systems to HalcyonAuto AI</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-2xl font-bold text-foreground">{connectedCount}/{totalCount}</div>
              <div className="text-sm text-muted-foreground">Systems Connected</div>
            </div>
            <Button className="btn-ai gap-2">
              <RefreshCw className="w-4 h-4" /> Sync All
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "DMS/CRM Connected", value: "2", icon: Database, color: "text-blue-500" },
            { label: "Data Points Synced", value: "24.5K", icon: Cloud, color: "text-emerald-500" },
            { label: "Active Webhooks", value: "12", icon: Link2, color: "text-purple-500" },
            { label: "API Calls Today", value: "8,432", icon: RefreshCw, color: "text-amber-500" },
          ].map((stat, idx) => (
            <Card key={idx}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg ${stat.color.replace('text-', 'bg-')}/10 flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Integration Categories */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(INTEGRATIONS).map(([key, category]) => (
            <Card key={key} className="hover:border-blue-500/30 transition-all" data-testid={`integration-${key}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className={`w-10 h-10 rounded-lg ${category.color.replace('text-', 'bg-')}/10 flex items-center justify-center`}>
                    <category.icon className={`w-5 h-5 ${category.color}`} />
                  </div>
                  <Badge variant="outline">
                    {category.integrations.filter(i => i.status === "connected").length}/{category.integrations.length}
                  </Badge>
                </div>
                <CardTitle className="text-lg mt-3">{category.title}</CardTitle>
                <CardDescription>{category.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {category.integrations.map((integration, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div>
                      <div className="font-medium text-foreground">{integration.name}</div>
                      {integration.lastSync && (
                        <div className="text-xs text-muted-foreground">
                          Last sync: {integration.lastSync} {integration.records && `• ${integration.records}`}
                        </div>
                      )}
                    </div>
                    {getStatusBadge(integration.status)}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Data Flow Diagram */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              Integration Architecture
            </CardTitle>
            <CardDescription>How HalcyonAuto connects to your dealership ecosystem</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative p-8 bg-slate-900/50 rounded-xl border border-slate-800">
              {/* Center Hub */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-teal-500 flex items-center justify-center z-10">
                <div className="text-center text-white">
                  <div className="font-display font-bold">HalcyonAuto</div>
                  <div className="text-xs opacity-80">AI Platform</div>
                </div>
              </div>

              {/* Integration Nodes */}
              <div className="grid grid-cols-3 gap-8 relative z-0">
                {[
                  { name: "DMS", icon: Database, position: "top-left" },
                  { name: "CRM", icon: Users, position: "top-center" },
                  { name: "F&I", icon: DollarSign, position: "top-right" },
                  { name: "Inventory", icon: Car, position: "middle-left" },
                  { name: "", icon: null, position: "center" },
                  { name: "Service", icon: Wrench, position: "middle-right" },
                  { name: "Marketing", icon: Megaphone, position: "bottom-left" },
                  { name: "Comms", icon: Phone, position: "bottom-center" },
                  { name: "Analytics", icon: BarChart3, position: "bottom-right" },
                ].map((node, idx) => (
                  <div key={idx} className={`flex flex-col items-center justify-center p-4 ${!node.name ? 'invisible' : ''}`}>
                    {node.icon && (
                      <>
                        <div className="w-16 h-16 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-2">
                          <node.icon className="w-8 h-8 text-slate-400" />
                        </div>
                        <div className="text-sm text-slate-400">{node.name}</div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Connection Lines (simplified) */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-64 border-2 border-dashed border-slate-700 rounded-full opacity-50"></div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4 text-center">
              <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <div className="text-2xl font-bold text-emerald-500">Real-time</div>
                <div className="text-sm text-muted-foreground">Bi-directional Sync</div>
              </div>
              <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <div className="text-2xl font-bold text-blue-500">REST + Webhooks</div>
                <div className="text-sm text-muted-foreground">API Architecture</div>
              </div>
              <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <div className="text-2xl font-bold text-amber-500">SOC 2 Type II</div>
                <div className="text-sm text-muted-foreground">Security Compliant</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
